
import { useState } from 'react';

interface IndustryDetail {
  id: string;
  title: string;
  icon: string;
  overview: string;
  features: string[];
  benefits: string[];
}

const industryDetails: IndustryDetail[] = [
  {
    id: 'defense',
    title: 'Defense Partners',
    icon: 'ri-shield-star-line',
    overview: 'We provide secure, mission-critical IT solutions specifically designed for defense contractors and government agencies. Our team understands the unique requirements of working with classified information and maintaining compliance with strict security protocols.',
    features: [
      'CMMC & NIST compliance solutions',
      'Secure classified network infrastructure',
      'Encrypted communication systems',
      'Threat intelligence & monitoring',
      'Secure cloud environments (FedRAMP)',
      'Identity & access management'
    ],
    benefits: [
      'Meet all government security requirements',
      'Protect sensitive and classified data',
      'Maintain operational continuity',
      'Reduce security audit preparation time',
      'Trusted partnership with cleared personnel'
    ]
  },
  {
    id: 'small-business',
    title: 'Small Business',
    icon: 'ri-store-2-line',
    overview: 'We help small businesses leverage enterprise-grade technology without the enterprise price tag. Our scalable solutions grow with your business, providing the IT foundation you need to compete effectively in today\'s digital marketplace.',
    features: [
      'Affordable managed IT services',
      'Cloud migration & optimization',
      'Business email & collaboration tools',
      'Website & e-commerce solutions',
      'Point-of-sale system integration',
      'Remote work enablement'
    ],
    benefits: [
      'Reduce IT costs by up to 40%',
      'Access to expert IT support',
      'Scale technology as you grow',
      'Focus on your core business',
      'Compete with larger competitors'
    ]
  },
  {
    id: 'manufacturing',
    title: 'Manufacturing',
    icon: 'ri-settings-3-line',
    overview: 'Transform your manufacturing operations with Industry 4.0 solutions. We integrate IoT sensors, automation systems, and data analytics to optimize production, reduce downtime, and improve quality control across your facilities.',
    features: [
      'IoT sensor deployment & monitoring',
      'Predictive maintenance systems',
      'Production line automation',
      'Quality control analytics',
      'Supply chain visibility',
      'ERP system integration'
    ],
    benefits: [
      'Reduce unplanned downtime by 50%',
      'Improve production efficiency',
      'Real-time visibility into operations',
      'Data-driven decision making',
      'Faster time-to-market'
    ]
  }
];

export default function Industries() {
  const [selectedIndustry, setSelectedIndustry] = useState<IndustryDetail | null>(null);

  const openModal = (industryId: string) => {
    const industry = industryDetails.find(i => i.id === industryId);
    if (industry) {
      setSelectedIndustry(industry);
    }
  };

  const closeModal = () => {
    setSelectedIndustry(null);
  };

  return (
    <section id="industries" className="py-12 sm:py-24 px-4 sm:px-8 bg-white">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <h2 className="text-3xl sm:text-5xl font-bold text-[#1A2332] mb-4">
            Industries We Serve
          </h2>
          <p className="text-base sm:text-lg text-gray-600">
            Tailored solutions for your sector
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 h-auto lg:h-[600px]">
          {/* Large Card - Defense Partners */}
          <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden group cursor-pointer row-span-1 lg:row-span-2 min-h-[300px] sm:min-h-[400px] lg:min-h-0">
            <img
              src="https://public.readdy.ai/ai/img_res/eaf6a75d697ff9dc4bc143a14c21c549.jpg"
              alt="Defense Partners"
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center mb-3 sm:mb-4">
                <i className="ri-shield-star-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2 sm:mb-3">
                Defense Partners
              </h3>
              <p className="text-white/90 text-sm sm:text-base mb-3 sm:mb-4 leading-relaxed">
                Secure, mission-critical solutions for defense contractors and government agencies
              </p>
              <button 
                onClick={() => openModal('defense')}
                className="inline-flex items-center text-white font-semibold hover:gap-2 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                Explore Solutions <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>

          {/* Top Right Card - Small Business */}
          <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100 p-6 sm:p-10 group cursor-pointer hover:shadow-xl transition-all">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-blue-600 flex items-center justify-center mb-4 sm:mb-6">
              <i className="ri-store-2-line text-2xl sm:text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-[#1A2332] mb-2 sm:mb-3">
              Small Business
            </h3>
            <p className="text-gray-700 text-sm sm:text-base mb-3 sm:mb-4 leading-relaxed">
              Affordable IT solutions that help small businesses compete and grow in the digital age
            </p>
            <button 
              onClick={() => openModal('small-business')}
              className="inline-flex items-center text-[#0066CC] font-semibold hover:gap-2 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
            >
              Learn More <i className="ri-arrow-right-line ml-1"></i>
            </button>
          </div>

          {/* Bottom Right Card - Manufacturing */}
          <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100 p-6 sm:p-10 group cursor-pointer hover:shadow-xl transition-all">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-blue-600 flex items-center justify-center mb-4 sm:mb-6">
              <i className="ri-settings-3-line text-2xl sm:text-3xl text-white"></i>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold text-[#1A2332] mb-2 sm:mb-3">
              Manufacturing
            </h3>
            <p className="text-gray-700 text-sm sm:text-base mb-3 sm:mb-4 leading-relaxed">
              Smart factory solutions and IoT integration for Industry 4.0 transformation
            </p>
            <button 
              onClick={() => openModal('manufacturing')}
              className="inline-flex items-center text-[#0066CC] font-semibold hover:gap-2 transition-all whitespace-nowrap cursor-pointer text-sm sm:text-base"
            >
              Learn More <i className="ri-arrow-right-line ml-1"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {selectedIndustry && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-2xl sm:rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#0066CC] to-[#004499] p-5 sm:p-8 rounded-t-2xl sm:rounded-t-3xl relative">
              <button 
                onClick={closeModal}
                className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-white"></i>
              </button>
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-white/20 flex items-center justify-center mb-3 sm:mb-4">
                <i className={`${selectedIndustry.icon} text-2xl sm:text-3xl text-white`}></i>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-white">{selectedIndustry.title}</h3>
            </div>

            {/* Modal Content */}
            <div className="p-5 sm:p-8">
              {/* Overview */}
              <div className="mb-8">
                <h4 className="text-xl font-bold text-[#1A2332] mb-3 flex items-center gap-2">
                  <i className="ri-information-line text-[#0066CC]"></i>
                  Overview
                </h4>
                <p className="text-gray-600 leading-relaxed">{selectedIndustry.overview}</p>
              </div>

              {/* Key Features */}
              <div className="mb-8">
                <h4 className="text-xl font-bold text-[#1A2332] mb-4 flex items-center gap-2">
                  <i className="ri-list-check-2 text-[#0066CC]"></i>
                  Key Features
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedIndustry.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 bg-gray-50 p-3 rounded-xl">
                      <i className="ri-checkbox-circle-fill text-green-500 mt-0.5"></i>
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Benefits */}
              <div className="mb-8">
                <h4 className="text-xl font-bold text-[#1A2332] mb-4 flex items-center gap-2">
                  <i className="ri-award-line text-[#0066CC]"></i>
                  Benefits
                </h4>
                <div className="space-y-3">
                  {selectedIndustry.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#0066CC]/10 flex items-center justify-center flex-shrink-0">
                        <i className="ri-star-fill text-[#0066CC] text-sm"></i>
                      </div>
                      <span className="text-gray-700">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-100">
                <a 
                  href="#contact" 
                  onClick={closeModal}
                  className="flex-1 bg-[#0066CC] text-white py-4 px-6 rounded-xl font-semibold text-center hover:bg-[#0055AA] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get Started
                </a>
                <button 
                  onClick={closeModal}
                  className="flex-1 bg-gray-100 text-gray-700 py-4 px-6 rounded-xl font-semibold text-center hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
